import React, { useState } from "react";
import { Modal, Fade, Box, Typography, TextField } from "@mui/material";
import CustomButton from "./Button";
import { ModalContent, style, StyledBackdrop } from "./CautionModal";
import { useTranslation } from "react-i18next";
import { getToken } from "./Pushes";
import useFetchFormData from "hooks/useFetchFormData";
import { toast } from "react-toastify";
import DOMPurify from "dompurify";

interface MetaTagsModalProps {
  openModal: boolean;
  closeModal: () => void;
  getPwas: () => void;
  id: string;
  pwaMeta: Record<string, string>[];
  pwaLinks?: Record<string, string>[];
}

type MetaConfig = {
  required: string[];
  allowed?: string[];
};

const META_RULES: Record<string, MetaConfig> = {
  default: {
    required: ["name", "content"],
    allowed: ["name", "content"],
  },
  charset: {
    required: ["charset"],
    allowed: ["charset"],
  },
  "http-equiv": {
    required: ["http-equiv", "content"],
    allowed: ["http-equiv", "content"],
  },
};

type LinkConfig = {
  required: string[];
  allowed: string[];
};
const LINK_RULES: LinkConfig = {
  required: ["rel", "href"],
  allowed: ["rel", "href"],
};

export const MetaTagsModal: React.FC<MetaTagsModalProps> = ({
  id,
  openModal,
  closeModal,
  pwaMeta,
  pwaLinks,
  getPwas,
}) => {
  const { t } = useTranslation();
  const token = getToken();
  const [error, setError] = useState<string>("");
  const initialInput = pwaMeta.map((tagObj) => getTagString(tagObj)).join("\n");
  const [metaTagsInput, setMetaTagsInput] = useState<string>(initialInput || "");

  const { triggerFetch: updatePWA } = useFetchFormData({
    url: `pwa/${id}`,
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const processMetaTags = () => {
    if (metaTagsInput.trim() === "") {
      setError("");
      submitMetaTags([]);
      return;
    }

    const doc = new DOMParser().parseFromString(`<html><head>${metaTagsInput}</head><body></body></html>`, "text/html");
    const metas = Array.from(doc.head.querySelectorAll("meta"));
    const links = Array.from(doc.head.querySelectorAll("link"));

    if (metas.length === 0 && links.length === 0) {
      setError(t("emptyMeta"));
      return;
    }

    for (const meta of metas) {
      const attributes = meta.getAttributeNames();
      let rule: MetaConfig;

      if (meta.hasAttribute("charset")) {
        rule = META_RULES.charset;
      } else if (meta.hasAttribute("http-equiv")) {
        rule = META_RULES["http-equiv"];
      } else {
        rule = META_RULES.default;
      }

      for (const req of rule.required) {
        if (!meta.hasAttribute(req) || !meta.getAttribute(req)!.trim()) {
          setError(t("metaTagsError"));
          return;
        }
      }

      for (const attr of attributes) {
        if (!rule.allowed!.includes(attr)) {
          setError(t("metaTagsError"));
          return;
        }
      }
    }

    for (const link of links) {
      const attrs = link.getAttributeNames();

      for (const req of LINK_RULES.required) {
        if (!link.hasAttribute(req) || !(link.getAttribute(req) || "").trim()) {
          setError(t("linkTagsError") || "Link tags must have rel and href");
          return;
        }
      }
      for (const attr of attrs) {
        if (!LINK_RULES.allowed.includes(attr)) {
          setError(t("linkTagsError") || "Link tag has disallowed attribute");
          return;
        }
      }
    }

    const metaObjects = metas.map((tag) => {
      const attributesObject: Record<string, string> = {};
      for (const name of tag.getAttributeNames()) {
        const raw = tag.getAttribute(name) || "";
        attributesObject[name] = DOMPurify.sanitize(raw);
      }
      return attributesObject;
    });

    const linkObjects = links.map((tag) => {
      const attributesObject: Record<string, string> = {};
      for (const name of tag.getAttributeNames()) {
        const raw = tag.getAttribute(name) || "";
        attributesObject[name] = DOMPurify.sanitize(raw);
      }
      return attributesObject;
    });

    const allTagObjectsArr = [...metaObjects, ...linkObjects];

    setError("");
    submitMetaTags(allTagObjectsArr);
  };

  const submitMetaTags = async (metaTagsArr: Record<string, string>[]) => {
    const data = new FormData();
    data.append("metaTags", JSON.stringify(metaTagsArr));
    await updatePWA(data);
    getPwas();
    closeModal();
    toast.success(t("successPwaUpdate"));
  };

  function getTagString(tagObj: Record<string, string>): string {
    const attrs = Object.entries(tagObj)
      .map(([key, val]) => `${key}="${val}"`)
      .join(" ");
    if ("rel" in tagObj) {
      return `<link ${attrs} />`;
    } else {
      return `<meta ${attrs} />`;
    }
  }

  return (
    <Modal
      open={openModal}
      onClose={closeModal}
      aria-labelledby="modal-meta-tags-title"
      aria-describedby="modal-meta-tags-description"
      slots={{ backdrop: StyledBackdrop }}
      closeAfterTransition
    >
      <Fade in={openModal}>
        <ModalContent
          sx={{
            ...style,
            width: "90%",
            maxWidth: "520px",
            padding: "32px 24px 24px 24px",
            borderRadius: "8px",
            bgcolor: "#FFFFFF",
            boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
            height: "auto",
            maxHeight: "90dvh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h2"
            sx={{
              marginBottom: "14px",
              fontSize: "22px",
              lineHeight: 1.2,
              fontWeight: 400,
              color: "#2E263D",
            }}
          >
            {t("nametatags")}
          </Typography>
          <Typography
            sx={{
              marginBottom: "8px",
              fontSize: "14px",
              lineHeight: "1.35",
              fontWeight: 400,
              color: "#2E263D",
            }}
          >
            {t("metaTagModalFirstDescription")}
          </Typography>
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "1.35",
                fontWeight: 500,
                color: "#2E263D",
              }}
            >
              {`<meta name="title" content="your-title" />`}
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "1.35",
                fontWeight: 500,
                color: "#2E263D",
              }}
            >
              {`<meta name="description" content="your-description" />`}
            </Typography>
            <Typography
              sx={{
                marginBottom: "8px",
                fontSize: "14px",
                lineHeight: "1.35",
                fontWeight: 500,
                color: "#2E263D",
              }}
            >
              {`<link rel="canonical" href="https://your-website.com" />`}
            </Typography>
            <Typography
              sx={{
                marginBottom: "32px",
                fontSize: "14px",
                lineHeight: "1.35",
                fontWeight: 400,
                color: "#2E263D",
              }}
            >
              {t("metaTagModalSecondDescription")}
            </Typography>
          </Box>
          <TextField
            multiline
            rows={10}
            onChange={(e) => setMetaTagsInput(e.target.value)}
            value={metaTagsInput}
            variant="outlined"
            placeholder={t("metaTagsPlaceholder")}
            fullWidth
            sx={{
              marginBottom: { xs: "24px", md: "32px" },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-root": {
                borderRadius: "8px",
                backgroundColor: "#f6f7fb",
              },
              textarea: {
                fontFamily: "var(--cygreRegular), sans-serif",
                fontSize: "14px",
                lineHeight: "22px",
                border: "none",
                color: "black",
                "&::placeholder": {
                  fontSize: "14px",
                  fontFamily: "var(--cygreRegular), sans-serif",
                  textOverflow: "ellipsis !important",
                  color: "#808080",
                },
              },
            }}
          />
          {error && (
            <Typography
              sx={{ color: "red", fontSize: "14px", marginBottom: { xs: "24px", md: "32px" }, textAlign: "center" }}
            >
              {error}
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              gap: "8px",
              flexDirection: { xs: "column-reverse", md: "row" },
              alignItems: "center",
            }}
          >
            <CustomButton
              textButton={t("cancel")}
              width="100%"
              height={40}
              customColor="#6D6777"
              customBorderColor="#C9D0D8"
              customHoverColor="transparent"
              customBorderRadius="8px"
              customFontSize="14px"
              customTextTransform="none"
              onClick={closeModal}
            />
            <CustomButton
              textButton={t("send")}
              width="100%"
              height={40}
              customBgColor="#F4701B"
              customHoverColor="#F4701B"
              customBorderRadius="8px"
              customFontSize="14px"
              customTextTransform="none"
              onClick={processMetaTags}
            />
          </Box>
        </ModalContent>
      </Fade>
    </Modal>
  );
};

export default MetaTagsModal;
